import { useEffect, useState, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2, ArrowRightLeft, AlertCircle } from 'lucide-react'
import { showError } from '@/lib/error'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form'
import { UserPickerDialog } from '@/components/user-select'
import { useTransferCustomer } from '@/hooks/useCustomers'
import { transferCustomerSchema, type TransferCustomerData } from '@/schemas/customer'
import type { Customer } from '@/types/customer'
import type { UserListItem } from '@/api/users'
import { usersApi, userSelectItemToListItem } from '@/api/users'

interface TransferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  customer: Customer | null
  onSuccess?: () => void
}

const renderSelectedUser = (user: UserListItem | null, placeholder: string) => (
  <span className={user ? 'truncate' : 'truncate text-muted-foreground'}>
    {user ? `${user.username}${user.phone ? ` · ${user.phone}` : ''}` : placeholder}
  </span>
)

export function TransferDialog({
  open,
  onOpenChange,
  customer,
  onSuccess,
}: TransferDialogProps) {
  const transferMutation = useTransferCustomer()

  // 用户选择器状态
  const [selectedLockUser, setSelectedLockUser] = useState<UserListItem | null>(null)
  const [selectedTrackerUser, setSelectedTrackerUser] = useState<UserListItem | null>(null)

  // 验证锁定人和跟踪人是否相同
  const isSameUser = useMemo(() => {
    if (selectedLockUser && selectedTrackerUser) {
      return selectedLockUser.id === selectedTrackerUser.id
    }
    return false
  }, [selectedLockUser, selectedTrackerUser])

  const form = useForm<TransferCustomerData>({
    resolver: zodResolver(transferCustomerSchema),
    defaultValues: {
      customer_id: 0,
      user_id: undefined,
      tracker_id: undefined,
    },
  })

  // 当客户数据变化时更新表单
  useEffect(() => {
    if (open && customer) {
      form.reset({
        customer_id: customer.id,
        user_id: customer.user_id || undefined,
        tracker_id: customer.tracker_id || undefined,
      })
      // 重置用户选择器状态
      setSelectedLockUser(null)
      setSelectedTrackerUser(null)

      // 根据 user_id 和 tracker_id 获取用户信息并回显
      const userIds: number[] = []
      // user_id 是必填字段，检查是否大于 0
      if (customer.user_id && customer.user_id > 0) userIds.push(customer.user_id)
      if (customer.tracker_id && customer.tracker_id > 0) userIds.push(customer.tracker_id)

      // 保存当前 customer 的引用，避免闭包问题
      const currentUserId = customer.user_id
      const currentTrackerId = customer.tracker_id

      if (userIds.length > 0) {
        usersApi.selectUsers({ user_ids: userIds, page_size: 100 })
          .then((res) => {
            const users = res.list
            // 设置锁定人
            if (currentUserId && currentUserId > 0) {
              const lockUser = users.find(u => u.id === currentUserId)
              if (lockUser) setSelectedLockUser(userSelectItemToListItem(lockUser))
            }
            // 设置跟踪人
            if (currentTrackerId && currentTrackerId > 0) {
              const trackerUser = users.find(u => u.id === currentTrackerId)
              if (trackerUser) setSelectedTrackerUser(userSelectItemToListItem(trackerUser))
            }
          })
          .catch((err) => {
            showError(err, '加载用户信息失败')
          })
      }
    }
  }, [open, customer, form])

  const onSubmit = async (data: TransferCustomerData) => {
    try {
      // 使用用户选择器选择的用户 ID
      const submitData = {
        ...data,
        user_id: selectedLockUser?.id ?? data.user_id,
        tracker_id: selectedTrackerUser?.id ?? data.tracker_id,
      }
      await transferMutation.mutateAsync(submitData)
      form.reset()
      setSelectedLockUser(null)
      setSelectedTrackerUser(null)
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      showError(error, '转移客户失败')
    }
  }

  const handleClose = () => {
    form.reset()
    setSelectedLockUser(null)
    setSelectedTrackerUser(null)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightLeft className="h-5 w-5" />
            转移客户
          </DialogTitle>
          <DialogDescription>
            转移客户"{customer?.name}"的锁定人或跟踪人
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="customer_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>客户信息</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={customer?.name || ''}
                      disabled
                      className="bg-muted"
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormItem>
              <FormLabel>新锁定人</FormLabel>
              <FormControl>
                <UserPickerDialog
                  value={selectedLockUser}
                  title="选择新锁定人"
                  confirmText="选择"
                  onConfirm={(users) => setSelectedLockUser(users[0] ?? null)}
                  trigger={
                    <Button type="button" variant="outline" className="w-full justify-start">
                      {renderSelectedUser(selectedLockUser, '请选择新锁定人')}
                    </Button>
                  }
                />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel>新跟踪人</FormLabel>
              <FormControl>
                <UserPickerDialog
                  value={selectedTrackerUser}
                  title="选择新跟踪人"
                  confirmText="选择"
                  onConfirm={(users) => setSelectedTrackerUser(users[0] ?? null)}
                  trigger={
                    <Button type="button" variant="outline" className="w-full justify-start">
                      {renderSelectedUser(selectedTrackerUser, '请选择新跟踪人')}
                    </Button>
                  }
                />
              </FormControl>
            </FormItem>

            {/* 相同用户验证提示 */}
            {isSameUser && (
              <div className="flex items-center gap-2 text-sm text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span>锁定人与跟踪人不能是同一个人</span>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                取消
              </Button>
              <Button type="submit" disabled={transferMutation.isPending || isSameUser}>
                {transferMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                确认转移
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
